import { useState, useEffect } from 'react'
import { Plus, Trash2, ExternalLink, RefreshCw, Image as ImageIcon, Download, Search, CheckSquare, Square, Folder, Grid, Eye } from 'lucide-react'
import api from '../services/api'

interface HubService {
  id: string
  name: string
  url: string
  icon: string
  description: string
  category: string
  created_at: string
}

interface CapturedImage {
  name: string
  path: string
  size_kb: number
  modified_at: number
  folder: string
  source_domain?: string
  source_url?: string
  note_title?: string
  tags?: string[]
  week_number?: string
  week_id?: string
  date_formatted?: string
  month_year?: string
  group?: string
  region?: string
  region_key?: string
  postal_code?: string
  venue?: string
  note_path?: string
  is_pics?: boolean
  sync_state?: 'brute' | 'locale' | 'prête' | null
  sync_renamed_to?: string
  sync_captures_path?: string
  sync_resized?: boolean
  sync_final_size_kb?: number
}

interface RegionInfo {
  id: string
  name: string
  depts: string[]
  keywords: string[]
}

const REGIONS_LIST: RegionInfo[] = [
  {
    id: "ALSACE",
    name: "ALSACE (67-68)",
    depts: ["67", "68"],
    keywords: ["alsace", "colmar", "strasbourg", "mulhouse", "haguenau", "sélestat", "bas-rhin", "haut-rhin"]
  },
  {
    id: "AQUITAINE",
    name: "AQUITAINE (24-33-40-47-64)",
    depts: ["24", "33", "40", "47", "64"],
    keywords: ["aquitaine", "bordeaux", "sainte-hélène", "pau", "merignac", "pessac", "bayonne", "dax", "dordogne", "gironde", "landes", "lot-et-garonne", "pyrénées-atlantiques"]
  },
  {
    id: "CHAMPAGNE_ARDENNES",
    name: "CHAMPAGNE ARDENNES (08-10-51-52)",
    depts: ["08", "10", "51", "52"],
    keywords: ["champagne", "ardennes", "reims", "troyes", "charleville", "chalons", "chaumont", "marne", "haute-marne"]
  },
  {
    id: "AUVERGNE",
    name: "AUVERGNE (03-15-43-63)",
    depts: ["03", "15", "43", "63"],
    keywords: ["auvergne", "clermont", "montlucon", "aurillac", "vichy", "le puy", "allier", "cantal", "haute-loire", "puy-de-dôme"]
  },
  {
    id: "BOURGOGNE",
    name: "BOURGOGNE (21-58-71-89)",
    depts: ["21", "58", "71", "89"],
    keywords: ["bourgogne", "dijon", "nevers", "chalon", "auxerre", "macon", "côte-d'or", "nièvre", "saône-et-loire", "yonne"]
  },
  {
    id: "BRETAGNE",
    name: "BRETAGNE (22-29-35-56)",
    depts: ["22", "29", "35", "56"],
    keywords: ["bretagne", "breizh", "rennes", "brest", "lorient", "vannes", "quimper", "saint-malo", "saint-brieuc", "côtes-d'armor", "finistère", "ille-et-vilaine", "morbihan"]
  },
  {
    id: "CENTRE",
    name: "CENTRE (18-28-36-37-41-45)",
    depts: ["18", "28", "36", "37", "41", "45"],
    keywords: ["centre", "val de loire", "orleans", "tours", "bourges", "blois", "chartres", "chateauroux", "cher", "eure-et-loir", "indre", "indre-et-loire", "loir-et-cher", "loiret"]
  },
  {
    id: "FRANCHE_COMTE",
    name: "FRANCHE COMTE (25-39-70-90)",
    depts: ["25", "39", "70", "90"],
    keywords: ["franche", "comte", "besancon", "belfort", "montbeliard", "dole", "vesoul", "doubs", "jura", "haute-saône", "territoire de belfort"]
  },
  {
    id: "LANGUEDOC_ROUSSILLON",
    name: "LANGUEDOC ROUSSILLON (11-30-34-48-66)",
    depts: ["11", "30", "34", "48", "66"],
    keywords: ["languedoc", "roussillon", "montpellier", "nimes", "perpignan", "narbonne", "carcassonne", "mende", "aude", "gard", "hérault", "lozère", "pyrénées-orientales"]
  },
  {
    id: "LIMOUSIN",
    name: "LIMOUSIN (19-23-87)",
    depts: ["19", "23", "87"],
    keywords: ["limousin", "limoges", "brive", "tulle", "gueret", "corrèze", "creuse", "haute-vienne"]
  },
  {
    id: "LORRAINE",
    name: "LORRAINE (54-55-57-88)",
    depts: ["54", "55", "57", "88"],
    keywords: ["lorraine", "metz", "nancy", "thionville", "epinal", "bar-le-duc", "meurthe-et-moselle", "meuse", "moselle", "vosges"]
  },
  {
    id: "MIDI_PYRENEES",
    name: "MIDI PYRENEES (09-12-31-32-46-65-81-82)",
    depts: ["09", "12", "31", "32", "46", "65", "81", "82"],
    keywords: ["midi-pyrénées", "midi pyrenees", "toulouse", "montauban", "albi", "tarbes", "rodez", "castres", "cahors", "foix", "ariège", "aveyron", "haute-garonne", "gers", "lot", "hautes-pyrénées", "tarn", "tarn-et-garonne"]
  },
  {
    id: "NORMANDIE",
    name: "NORMANDIE (14-27-50-61-76)",
    depts: ["14", "27", "50", "61", "76"],
    keywords: ["normandie", "rouen", "caen", "le havre", "cherbourg", "evreux", "alencon", "calvados", "eure", "manche", "orne", "seine-maritime"]
  },
  {
    id: "NORD_HAUT_DE_FRANCE",
    name: "Nord - Haut-de-France (02-59-60-62-80)",
    depts: ["02", "59", "60", "62", "80"],
    keywords: ["nord", "hauts-de-france", "picardie", "lille", "amiens", "valenciennes", "dunkerque", "calais", "arras", "beauvais", "saint-quentin", "aisne", "oise", "pas-de-calais", "somme"]
  },
  {
    id: "PAYS_DE_LA_LOIRE",
    name: "PAYS DE LA LOIRE (44-49-53-72-85)",
    depts: ["44", "49", "53", "72", "85"],
    keywords: ["pays de la loire", "loire", "nantes", "angers", "le mans", "laval", "la roche-sur-yon", "loire-atlantique", "maine-et-loire", "mayenne", "sarthe", "vendée"]
  },
  {
    id: "PACA",
    name: "PACA (04-05-06-13-83-84)",
    depts: ["04", "05", "06", "13", "83", "84"],
    keywords: ["paca", "provence", "marseille", "nice", "toulon", "aix", "avignon", "cannes", "antibes", "gap", "digne", "alpes-de-haute-provence", "hautes-alpes", "alpes-maritimes", "bouches-du-rhône", "var", "vaucluse"]
  },
  {
    id: "POITOU_CHARENTES",
    name: "POITOU-CHARENTES (16-17-79-86)",
    depts: ["16", "17", "79", "86"],
    keywords: ["poitou-charentes", "poitou charentes", "poitiers", "la rochelle", "niort", "angouleme", "charente", "charente-maritime", "deux-sèvres", "vienne"]
  },
  {
    id: "PARIS_ILE_DE_FRANCE",
    name: "PARIS ILE DE FRANCE (75-77-78-91-92-93-94-95)",
    depts: ["75", "77", "78", "91", "92", "93", "94", "95"],
    keywords: ["paris", "ile-de-france", "idf", "versailles", "boulogne", "nanterre", "creteil", "saint-denis", "argenteuil", "evry", "seine-et-marne", "yvelines", "essonne", "hauts-de-seine", "seine-saint-denis", "val-de-marne", "val-d'oise"]
  },
  {
    id: "RHONES_ALPES",
    name: "RHONES –ALPES (01-07-26-38-42-69-73-74)",
    depts: ["01", "07", "26", "38", "42", "69", "73", "74"],
    keywords: ["rhône-alpes", "rhone alpes", "lyon", "grenoble", "saint-etienne", "villeurbanne", "valence", "chambery", "annecy", "ain", "ardèche", "drôme", "isère", "loire", "rhône", "savoie", "haute-savoie"]
  },
  {
    id: "DOM_TOM",
    name: "DOM TOM",
    depts: ["971", "972", "973", "974", "975", "976", "977", "978", "984", "986", "987", "988", "989"],
    keywords: ["dom tom", "dom-tom", "guadeloupe", "martinique", "guyane", "reunion", "mayotte", "nouvelle-caledonie", "polynesie"]
  }
]

const getRegionKeyForImage = (img: CapturedImage): string | null => {
  // 0. Clé région directe depuis le chemin CAPTURES/SEMAINES/YYYY-WWW/{REGION}/
  if (img.region_key) return img.region_key;

  // 1. Par code postal issu du backend
  const postalCode = img.postal_code || "";
  if (postalCode) {
    const dept = postalCode.slice(0, 2);
    const dept3 = postalCode.slice(0, 3);
    
    for (const reg of REGIONS_LIST) {
      if (reg.depts.includes(dept3) || reg.depts.includes(dept)) {
        return reg.id;
      }
    }
  }

  // 2. Par détection du département dans le nom ou le titre
  const pathDeptMatch = img.path.match(/\b(0[1-9]|[1-8]\d|9[0-5]|97[1-6]|98[4-8])\b/);
  const nameDeptMatch = img.name.match(/\b(0[1-9]|[1-8]\d|9[0-5]|97[1-6]|98[4-8])\b/);
  const titleDeptMatch = img.note_title ? img.note_title.match(/\b(0[1-9]|[1-8]\d|9[0-5]|97[1-6]|98[4-8])\b/) : null;
  const deptMatch = pathDeptMatch || nameDeptMatch || titleDeptMatch;

  if (deptMatch) {
    const dept = deptMatch[1];
    for (const reg of REGIONS_LIST) {
      if (reg.depts.includes(dept)) {
        return reg.id;
      }
    }
  }

  // 3. Par mots-clés dans les métadonnées (ville, nom, etc.)
  const regionText = (img.region || "").toLowerCase();
  const pathText = img.path.toLowerCase();
  const nameText = img.name.toLowerCase();
  const titleText = (img.note_title || "").toLowerCase();

  for (const reg of REGIONS_LIST) {
    for (const keyword of reg.keywords) {
      if (regionText.includes(keyword) || 
          pathText.includes(keyword) || 
          nameText.includes(keyword) || 
          titleText.includes(keyword)) {
        return reg.id;
      }
    }
  }

  return null;
}

interface ServiceStatus {
  [key: string]: 'up' | 'down' | 'checking'
}

const CATEGORIES = ["Automation", "IA", "Data", "Outils", "Outils Dev"]

export default function CapturesPage() {
  const [loading, setLoading] = useState(true)

  // States for Grist sync
  const [showGristSyncModal, setShowGristSyncModal] = useState(false)
  const [syncingGrist, setSyncingGrist] = useState(false)
  const [syncGristResult, setSyncGristResult] = useState<any>(null)
  const [syncGristError, setSyncGristError] = useState<string | null>(null)
  const [selectedGristFolder, setSelectedGristFolder] = useState<string>('')

  // Sync SDM → À venir
  const [syncingSDM, setSyncingSDM] = useState(false)
  const [syncSDMResult, setSyncSDMResult] = useState<{ importes: number; message: string } | null>(null)

  const GRIST_FOLDERS = [
    { group: 'Concerts',          folder: '01-concerts_metal',        label: 'concerts-metal.com' },
    { group: 'Concerts',          folder: '02-sueur-de-metal',        label: 'Sueur de Métal' },
    { group: 'Concerts',          folder: '04-oukonva',               label: 'Oukonva' },
    { group: 'Concerts',          folder: '05-breizh_metal',          label: 'Breizh Metal' },
    { group: 'Concerts',          folder: '06-festivals_rock',        label: 'Festivals Rock' },
    { group: 'Concerts',          folder: '07-conventions_disques',   label: 'Conventions Disques' },
    { group: 'Facebook',          folder: '03-facebook/concerts',     label: 'Facebook — Concerts' },
    { group: 'Facebook',          folder: '03-facebook/presse',       label: 'Facebook — Presse' },
    { group: 'Facebook',          folder: '03-facebook/audio',        label: 'Facebook — Audio' },
    { group: 'Facebook',          folder: '03-facebook/video',        label: 'Facebook — Vidéo' },
    { group: 'Facebook',          folder: '03-facebook/video_live',   label: 'Facebook — Vidéo Live' },
    { group: 'Facebook',          folder: '03-facebook/documentaire', label: 'Facebook — Documentaire' },
    { group: 'Facebook',          folder: '03-facebook/radio',        label: 'Facebook — Radio' },
    { group: 'Médias généraux',   folder: '08-presse',                label: 'Presse' },
    { group: 'Médias généraux',   folder: '09-audio',                 label: 'Audio' },
    { group: 'Médias généraux',   folder: '10-video',                 label: 'Vidéo' },
    { group: 'Médias généraux',   folder: '11-documentaire',          label: 'Documentaire' },
    { group: 'Médias généraux',   folder: '12-radio',                 label: 'Radio' },
    { group: 'Médias généraux',   folder: '13-video_live',            label: 'Vidéo Live' },
  ]

  // ── État section Sources (archives) ──
  const [archiveCounts, setArchiveCounts] = useState<Record<string, number>>({})
  const [selectedArchiveSource, setSelectedArchiveSource] = useState<string | null>(null)
  const [archiveImages, setArchiveImages] = useState<CapturedImage[]>([])
  const [loadingArchive, setLoadingArchive] = useState(false)
  const [selectedArchiveWeek, setSelectedArchiveWeek] = useState<string>('all')
  const [selectedArchiveSize, setSelectedArchiveSize] = useState<string>('all')
  const [selectedArchivePaths, setSelectedArchivePaths] = useState<string[]>([])
  const [deletingArchive, setDeletingArchive] = useState(false)

  // ── État section Facebook catégories ──
  const [fbCounts, setFbCounts] = useState<Record<string, number>>({})
  const [selectedFbCat, setSelectedFbCat] = useState<string | null>(null)
  const [fbImages, setFbImages] = useState<CapturedImage[]>([])
  const [loadingFb, setLoadingFb] = useState(false)
  const [selectedFbWeek, setSelectedFbWeek] = useState<string>('all')

  // ── État section Captures traitées (SEMAINES/) ──
  const [semaineCounts, setSemaineCounts] = useState<Record<string, number>>({})
  const [selectedSemaineRegion, setSelectedSemaineRegion] = useState<string | null>(null)
  const [selectedSemaineWeek, setSelectedSemaineWeek] = useState<string>('all')
  const [semaineImages, setSemaineImages] = useState<CapturedImage[]>([])
  const [loadingSemaine, setLoadingSemaine] = useState(false)

  // ── État section Captures (traitées) — legacy ──
  const [images, setImages] = useState<CapturedImage[]>([])
  const [sourceCounts, setSourceCounts] = useState<Record<string, number>>({})
  const [selectedImages, setSelectedImages] = useState<string[]>([])
  const [loadingImages, setLoadingImages] = useState(false)
  const [imageSearch, setImageSearch] = useState('')
  const [previewImage, setPreviewImage] = useState<CapturedImage | null>(null)
  const [selectedFolder, setSelectedFolder] = useState<string>('all')
  const [selectedSourceDomain, setSelectedSourceDomain] = useState<string>('all')
  // Filtres en cascade : Source → Région → Semaine
  const [selectedSource, setSelectedSource] = useState<string | null>(null)
  const [selectedWeek, setSelectedWeek] = useState<string>('all')
  const [selectedRegion, setSelectedRegion] = useState<string>('all')
  const [movingImage, setMovingImage] = useState<CapturedImage | null>(null)
  const [moveTargetFolder, setMoveTargetFolder] = useState<string>('')
  const [vaultFolders, setVaultFolders] = useState<{id: string, label: string}[]>([])

  // States for Synchroniser (À publier → Master Grist)
  const [transfertBilletterie, setTransfertBilletterie] = useState(false)
  const [transfertResult, setTransfertResult] = useState<{ total_importes: number; message: string; errors: string[] } | null>(null)
  const [aPublierCount, setAPublierCount] = useState<number | null>(null)

  // States for Synchro PICS (kept for reference — button replaced)
  const [syncingPics, setSyncingPics] = useState(false)
  const [syncPicsResult, setSyncPicsResult] = useState<any>(null)
  const [showSyncPicsResult, setShowSyncPicsResult] = useState(false)

  // States for Manual Capture Upload
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadTags, setUploadTags] = useState('capture,manuel')
  const [uploadingImage, setUploadingImage] = useState(false)

  const fetchAPublierCount = async () => {
    try {
      const r = await api.get('/api/veilles/a-publier/count')
      setAPublierCount(r.data.total)
    } catch { /* silencieux */ }
  }

  // Load counts + folders on mount (léger), images uniquement sur sélection source
  useEffect(() => {
    fetchArchiveCounts()
    fetchSourceCounts()
    fetchFacebookCounts()
    fetchSemaineCounts()
    fetchFolders()
    fetchAPublierCount()
  }, [])

  useEffect(() => {
    if (selectedSemaineRegion !== null) fetchSemaineImages(selectedSemaineRegion)
    else { setSemaineImages([]); setSelectedSemaineWeek('all') }
  }, [selectedSemaineRegion])

  useEffect(() => {
    if (selectedArchiveSource !== null) fetchArchiveImages(selectedArchiveSource)
    else { setArchiveImages([]); setSelectedArchiveWeek('all'); setSelectedArchiveSize('all'); setSelectedArchivePaths([]) }
  }, [selectedArchiveSource])

  useEffect(() => {
    if (selectedFbCat !== null) fetchFacebookImages(selectedFbCat)
    else { setFbImages([]); setSelectedFbWeek('all') }
  }, [selectedFbCat])

  useEffect(() => {
    if (selectedSource !== null) fetchImages(selectedSource)
    else setImages([])
  }, [selectedSource])

  const fetchFolders = async () => {
    try {
      const response = await api.get('/api/obsidian-clip/folders')
      setVaultFolders(response.data)
    } catch (err) {
      console.error('Erreur chargement dossiers:', err)
    }
  }

  const handleMoveImage = async () => {
    if (!movingImage || !moveTargetFolder) return
    try {
      await api.post('/api/obsidian-clip/images/move', {
        image_path: movingImage.path,
        target_folder: moveTargetFolder
      })
      setImages(prev => prev.filter(i => i.path !== movingImage.path))
      if (previewImage?.path === movingImage.path) setPreviewImage(null)
      setMovingImage(null)
      setMoveTargetFolder('')
      fetchImages()
    } catch (err: any) {
      alert('Erreur déplacement : ' + (err.response?.data?.detail || err.message))
    }
  }

  const fetchArchiveCounts = async () => {
    try {
      const response = await api.get('/api/obsidian-clip/images/archive-counts')
      setArchiveCounts(response.data)
    } catch (err) {
      console.error('Erreur archive-counts:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchArchiveImages = async (src: string) => {
    try {
      setLoadingArchive(true)
      const response = await api.get(`/api/obsidian-clip/images/archive?source=${encodeURIComponent(src)}`)
      setArchiveImages(response.data)
    } catch (err) {
      console.error('Erreur archive images:', err)
    } finally {
      setLoadingArchive(false)
    }
  }

  const fetchSourceCounts = async () => {
    try {
      const response = await api.get('/api/obsidian-clip/images/counts')
      setSourceCounts(response.data)
    } catch (err) {
      console.error('Erreur chargement counts:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchSemaineCounts = async () => {
    try {
      const response = await api.get('/api/obsidian-clip/images/semaines-counts')
      setSemaineCounts(response.data)
    } catch (err) {
      console.error('Erreur semaines-counts:', err)
    }
  }

  const fetchSemaineImages = async (region: string) => {
    try {
      setLoadingSemaine(true)
      const response = await api.get(`/api/obsidian-clip/images/semaines?region=${encodeURIComponent(region)}`)
      setSemaineImages(response.data)
    } catch (err) {
      console.error('Erreur semaines images:', err)
    } finally {
      setLoadingSemaine(false)
    }
  }

  const fetchFacebookCounts = async () => {
    try {
      const response = await api.get('/api/obsidian-clip/images/facebook-counts')
      setFbCounts(response.data)
    } catch (err) {
      console.error('Erreur facebook-counts:', err)
    }
  }

  const fetchFacebookImages = async (cat: string) => {
    try {
      setLoadingFb(true)
      const response = await api.get(`/api/obsidian-clip/images/facebook?cat=${encodeURIComponent(cat)}`)
      setFbImages(response.data)
    } catch (err) {
      console.error('Erreur facebook images:', err)
    } finally {
      setLoadingFb(false)
    }
  }

  const fetchImages = async (src: string) => {
    try {
      setLoadingImages(true)
      const response = await api.get(`/api/obsidian-clip/images?source=${encodeURIComponent(src)}`)
      setImages(response.data)
    } catch (err) {
      console.error('Erreur chargement images:', err)
    } finally {
      setLoadingImages(false)
    }
  }

  const handleDeleteArchiveBatch = async (paths: string[]) => {
    if (paths.length === 0) return
    if (!window.confirm(`Supprimer définitivement ${paths.length} image${paths.length > 1 ? 's' : ''} ?`)) return
    try {
      setDeletingArchive(true)
      const query = paths.map(p => `paths=${encodeURIComponent(p)}`).join('&')
      await api.delete(`/api/obsidian-clip/images/batch?${query}`)
      setArchiveImages(prev => prev.filter(img => !paths.includes(img.path)))
      setSelectedArchivePaths([])
    } catch (err: any) {
      alert('Erreur suppression : ' + (err.response?.data?.detail || err.message))
    } finally {
      setDeletingArchive(false)
    }
  }

  const handleTransfertBilletterie = async () => {
    setTransfertBilletterie(true)
    setTransfertResult(null)
    try {
      const [transfertResp, picsResp] = await Promise.allSettled([
        api.post('/api/veilles/transfert-billetterie'),
        api.post('/api/obsidian-clip/images/synchroniser'),
      ])
      const t = transfertResp.status === 'fulfilled' ? transfertResp.value.data : { total_importes: 0, errors: ['Erreur transfert'] }
      const p = picsResp.status === 'fulfilled' ? picsResp.value.data : null
      const picsMsg = p && (p.processed ?? 0) > 0 ? ` · ${p.processed} PICS compressées` : ''
      setTransfertResult({ ...t, message: (t.message || '') + picsMsg })
      if (p) fetchImages()
      fetchAPublierCount()
    } catch (err: any) {
      setTransfertResult({ total_importes: 0, message: err.response?.data?.detail || 'Erreur transfert.', errors: [] })
    } finally {
      setTransfertBilletterie(false)
    }
  }

  const handleSyncPics = async () => {
    setSyncingPics(true)
    setSyncPicsResult(null)
    try {
      const response = await api.post('/api/obsidian-clip/images/synchroniser')
      setSyncPicsResult(response.data)
      setShowSyncPicsResult(true)
      fetchImages()
    } catch (err: any) {
      alert('Erreur synchronisation : ' + (err.response?.data?.detail || err.message))
    } finally {
      setSyncingPics(false)
    }
  }

  const handleSyncGrist = async () => {
    if (!selectedGristFolder) return
    try {
      setSyncingGrist(true)
      setSyncGristError(null)
      setSyncGristResult(null)
      const response = await api.post('/api/obsidian-clip/sync-all-grist', { folder: selectedGristFolder })
      setSyncGristResult(response.data)
    } catch (err: any) {
      setSyncGristError(err.response?.data?.detail || 'Erreur lors de la synchronisation.')
    } finally {
      setSyncingGrist(false)
    }
  }

  const handleSyncSDM = async () => {
    try {
      setSyncingSDM(true)
      setSyncSDMResult(null)
      const response = await api.post('/api/veilles/sdm/sync-to-avenir')
      setSyncSDMResult(response.data)
    } catch (err: any) {
      setSyncSDMResult({ importes: 0, message: err.response?.data?.detail || 'Erreur sync SDM.' })
    } finally {
      setSyncingSDM(false)
    }
  }

  const getStatusText = (status?: 'up' | 'down' | 'checking') => {
    if (status === 'up') return '● En ligne'
    if (status === 'down') return '● Hors ligne'
    return '● Vérification...'
  }

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Automation': '#3b82f6',  // blue
      'IA': '#8b5cf6',           // purple
      'Data': '#10b981',         // teal
      'Outils': '#f59e0b',        // amber
      'Outils Dev': '#ec4899'     // pink
    }
    return colors[category] || '#6b7280'
  }

  // Image helpers
  const toggleSelectImage = (path: string) => {
    setSelectedImages(prev =>
      prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
    )
  }

  const handleSelectAll = (filteredImagesList: CapturedImage[]) => {
    const filteredPaths = filteredImagesList.map(img => img.path)
    const allSelected = filteredPaths.length > 0 && filteredPaths.every(path => selectedImages.includes(path))
    if (allSelected) {
      setSelectedImages(prev => prev.filter(path => !filteredPaths.includes(path)))
    } else {
      setSelectedImages(prev => Array.from(new Set([...prev, ...filteredPaths])))
    }
  }

  const handleDownloadBatch = () => {
    if (selectedImages.length === 0) return
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8100'
    const query = selectedImages.map(p => `paths=${encodeURIComponent(p)}`).join('&')
    window.open(`${API_URL}/api/obsidian-clip/images/download-batch?${query}`, '_blank')
  }

  const handleDownloadSingle = (img: CapturedImage) => {
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8100'
    
    // Construire un nom de fichier premium pour l'utilisateur : "Date - Groupe - Lieu.ext"
    let downloadName = img.name
    if (img.group) {
      const parts = []
      if (img.date_formatted) {
        parts.push(img.date_formatted)
      }
      parts.push(img.group)
      if (img.venue) {
        parts.push(img.venue)
      }
      const ext = img.name.substring(img.name.lastIndexOf('.'))
      // Vérifier s'il y a un index de photo (ex: -1, -2) à la fin
      const baseName = img.name.substring(0, img.name.lastIndexOf('.'))
      const idxMatch = baseName.match(/-(\d+)$/)
      const suffix = idxMatch ? `-${idxMatch[1]}` : ""
      
      downloadName = parts.join(' - ') + suffix + ext
      // Remplacer les caractères non supportés par les OS
      downloadName = downloadName.replace(/[\\/*?:"<>|]/g, '_')
    }

    // Utiliser le téléchargement natif forcé de l'API pour contourner le CORS et le Mixed-Content
    window.location.href = `${API_URL}/api/obsidian-clip/images/file/${encodeURIComponent(img.path)}?download=true&name=${encodeURIComponent(downloadName)}`
  }

  const handleDeleteImage = async (img: CapturedImage) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer définitivement cette image ainsi que sa note Obsidian associée ?\n\nImage : ${img.name}`)) {
      return
    }
    
    try {
      await api.delete(`/api/obsidian-clip/images/file/${encodeURIComponent(img.path)}`)
      
      // Mettre à jour l'état local pour faire disparaître l'image de la galerie
      setImages(prevImages => prevImages.filter(i => i.path !== img.path))
      
      // Fermer l'aperçu si nécessaire
      if (previewImage?.path === img.path) {
        setPreviewImage(null)
      }
    } catch (err) {
      console.error("Erreur suppression image:", err)
      alert("Impossible de supprimer l'image.")
    }
  }

  const handleUploadCapture = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUploadFile) return

    setUploadingImage(true)
    const formDataObj = new FormData()
    formDataObj.append('file', selectedUploadFile)
    formDataObj.append('title', uploadTitle || 'Capture d\'écran')
    formDataObj.append('tags', uploadTags)

    try {
      await api.post('/api/obsidian-clip/images/upload', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      alert('Capture d\'écran importée et indexée dans Obsidian avec succès !')
      setSelectedUploadFile(null)
      setUploadTitle('')
      setUploadTags('capture,manuel')
      fetchImages()
    } catch (err: any) {
      console.error("Erreur d'importation de la capture:", err)
      alert("Erreur lors de l'importation de la capture : " + (err.response?.data?.detail || err.message))
    } finally {
      setUploadingImage(false)
    }
  }


  // Liste fixe de toutes les sources scrappées (affichées même à 0)
  // count = archiveCounts[domain] pour archives, sourceCounts[domain] pour vault principal
  const SCRAPE_SOURCES: { domain: string; label: string; emoji: string; gristUrl?: string; countFrom?: 'source' }[] = [
    { domain: 'sueurdemetal.com',   label: 'Sueur de Métal',      emoji: '🤘', gristUrl: 'https://grist.megagenda.art/doc/kBBM3mzRp8z6SRewka15AB' },
    { domain: 'concerts-metal.com', label: 'concerts-metal.com',  emoji: '🎸', gristUrl: 'https://grist.megagenda.art/doc/g4ATXQnhCBctG2ahdsAuw2' },
    { domain: 'breizhmetalweb.fr',  label: 'Breizh Metal',        emoji: '🤘', gristUrl: 'https://grist.megagenda.art/doc/sNGnhXX1JapTCB3rkH9nEe' },
    { domain: 'oukonva.com',        label: 'Oukonva',             emoji: '🎵', gristUrl: 'https://grist.megagenda.art/doc/8z8TbnoNwXht5b8SGXg3Wq' },

    { domain: 'foires-disques.fr',  label: 'Foires aux Disques',  emoji: '💿', gristUrl: 'https://grist.megagenda.art/nZpBRfjkc4wr/FMA-Source-Foires-Disques' },
    { domain: 'facebook.com',       label: 'FB Archives',         emoji: '📘' },
    { domain: 'autres-sources',     label: 'Autres Sources',      emoji: '📦', countFrom: 'source' },
    { domain: 'inconnu',            label: 'À Trier',             emoji: '❓', countFrom: 'source' },
  ]

  // Catégories Facebook (ordre Grist sync)
  const FB_CATEGORIES: { cat: string; label: string; emoji: string; gristUrl: string }[] = [
    { cat: 'concerts',    label: 'Concerts',    emoji: '🎸', gristUrl: 'https://grist.megagenda.art/doc/e37onJLApZFD3Yz6KZM2hw' },
    { cat: 'presse',      label: 'Presse',      emoji: '📰', gristUrl: 'https://grist.megagenda.art/doc/2sJEmH7oKXRxfxCjJGyWVN' },
    { cat: 'audio',       label: 'Audio',       emoji: '🎵', gristUrl: 'https://grist.megagenda.art/doc/dnHN1Jp1XxgUQtyatcDYZS' },
    { cat: 'video',       label: 'Vidéo',       emoji: '🎬', gristUrl: 'https://grist.megagenda.art/doc/aiitxa2b4HanuAKv5kmdD3' },
    { cat: 'video_live',  label: 'Vidéo Live',  emoji: '📡', gristUrl: 'https://grist.megagenda.art/doc/tLLCrZR7MU2JAJbEpTc8Uz' },
    { cat: 'documentaire',label: 'Documentaire',emoji: '🎞️', gristUrl: 'https://grist.megagenda.art/doc/8MrKgQi3qyxjSTzUUdsAjH' },
    { cat: 'radio',       label: 'Radio',       emoji: '📻', gristUrl: 'https://grist.megagenda.art/doc/fC63Q3gspaNDVcnvd9mg4o' },
  ]

  // Labels des domaines source
  const SOURCE_LABELS: Record<string, string> = {
    'facebook.com':       '📘 FB Archives',
    'autres-sources':     '📦 Autres Sources',
    'inconnu':            '❓ À Trier',
    'concerts-metal.com': '🎸 concerts-metal.com',
    'sueurdemetal.com':   '🤘 Sueur de Métal',
    'breizhmetalweb.fr':  '🤘 Breizh Metal',
    'oukonva.com':        '🎵 Oukonva',
    'festivals-rock.fr':  '🎪 Festivals Rock',
    'foires-disques.fr':  '💿 Foires aux Disques',
    'presse':             '📰 Presse',
    'audio':              '🎵 Audio',
    'video':              '🎬 Vidéo',
    'documentaire':       '🎞️ Documentaire',
    'radio':              '📻 Radio',
    'video-live':         '📡 Vidéo Live',
  }

  // Liens Grist par source scrappée
  const SOURCE_GRIST_URLS: Record<string, string> = {
    'sueurdemetal.com':   'https://grist.megagenda.art/doc/kBBM3mzRp8z6SRewka15AB',
    'concerts-metal.com': 'https://grist.megagenda.art/doc/g4ATXQnhCBctG2ahdsAuw2',
    'breizhmetalweb.fr':  'https://grist.megagenda.art/doc/sNGnhXX1JapTCB3rkH9nEe',
    'oukonva.com':        'https://grist.megagenda.art/doc/8z8TbnoNwXht5b8SGXg3Wq',
    'festivals-rock.fr':  'https://grist.megagenda.art/doc/ezAZLUh55DGg8v7LtJVtZa',
    'foires-disques.fr':  'https://grist.megagenda.art/nZpBRfjkc4wr/FMA-Source-Foires-Disques',
  }

  // ── Filtres en cascade Source → Semaine → Région ──────────────────────────
  // Sources disponibles depuis les compteurs (chargés au démarrage, indépendant des images)
  const availableSources = Object.keys(sourceCounts).filter(Boolean)

  // Étape 1 : filtre par source (rien si aucune source sélectionnée)
  const afterSourceFilter = selectedSource === null
    ? []
    : images.filter(img => img.source_domain === selectedSource)

  // Étape 2 : filtre par semaine
  const afterWeekFilter = selectedWeek === 'all'
    ? afterSourceFilter
    : afterSourceFilter.filter(img => img.week_number === selectedWeek)

  // Étape 3 : filtre par région
  const afterRegionFilter = selectedRegion === 'all'
    ? afterWeekFilter
    : afterWeekFilter.filter(img => getRegionKeyForImage(img) === selectedRegion)

  // Semaines disponibles pour la source sélectionnée
  const availableWeeks = Array.from(
    new Map(afterSourceFilter.map(img => [img.week_number, img])).values()
  ).filter(img => img.week_number)
   .sort((a, b) => (b.week_id || '').localeCompare(a.week_id || ''))

  // Régions disponibles pour la source+semaine sélectionnées
  const availableRegions = REGIONS_LIST.filter(reg =>
    afterWeekFilter.some(img => getRegionKeyForImage(img) === reg.id)
  )

  // Filtre final avec recherche texte
  const filteredImages = afterRegionFilter.filter(img => {
    const q = imageSearch.toLowerCase()
    return !q ||
      img.name.toLowerCase().includes(q) ||
      (img.note_title || '').toLowerCase().includes(q) ||
      (img.group || '').toLowerCase().includes(q) ||
      (img.venue || '').toLowerCase().includes(q)
  })

  const selectSource = (src: string | null) => {
    setSelectedSource(src)
    setSelectedWeek('all')
    setSelectedRegion('all')
  }
  const selectWeek = (week: string) => {
    setSelectedWeek(week)
    setSelectedRegion('all')
  }

  if (loading) {
    return (
      <div style={{ backgroundColor: '#111827', height: '100%', overflowY: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid #374151',
            borderTop: '4px solid #ec4899',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#d1d5db', marginTop: '16px' }}>Chargement...</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ backgroundColor: '#111827', height: '100%', overflowY: 'auto', padding: '32px' }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .service-card {
          background-color: #1f2937;
          border: 1px solid #374151;
          border-radius: 8px;
          padding: 20px;
          transition: all 0.3s ease;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .service-card:hover {
          background-color: #27323f;
          border-color: #4b5563;
          transform: translateY(-2px);
        }
        .category-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }
        @media (max-width: 1200px) {
          .category-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        @media (max-width: 768px) {
          .category-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 480px) {
          .category-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Image gallery custom CSS */
        .image-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }
        .image-card {
          background-color: #1f2937;
          border: 1px solid #374151;
          border-radius: 12px;
          overflow: hidden;
          position: relative;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .image-card:hover {
          transform: translateY(-4px);
          border-color: #ec4899;
          box-shadow: 0 10px 15px -3px rgba(236, 72, 153, 0.1), 0 4px 6px -2px rgba(236, 72, 153, 0.05);
        }
        .image-thumbnail-container {
          position: relative;
          width: 100%;
          padding-top: 75%; /* 4:3 Aspect Ratio */
          background-color: #111827;
          overflow: hidden;
          cursor: pointer;
        }
        .image-thumbnail {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .image-card:hover .image-thumbnail {
          transform: scale(1.08);
        }
        .image-card-checkbox {
          position: absolute;
          top: 10px;
          left: 10px;
          z-index: 10;
          width: 24px;
          height: 24px;
          border-radius: 6px;
          background-color: rgba(17, 24, 39, 0.8);
          border: 2px solid #4b5563;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          color: transparent;
          font-weight: bold;
          font-size: 14px;
        }
        .image-card-checkbox.selected {
          background-color: #ec4899;
          border-color: #ec4899;
          color: white;
        }
        .image-card-checkbox:hover {
          border-color: #ec4899;
          transform: scale(1.1);
        }
        .image-card-hover-actions {
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 10;
          display: flex;
          gap: 6px;
          opacity: 0;
          transition: opacity 0.2s;
        }
        .image-card:hover .image-card-hover-actions {
          opacity: 1;
        }
        .image-card-action-btn {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          background-color: rgba(17, 24, 39, 0.85);
          border: 1px solid #374151;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        .image-card-action-btn:hover {
          background-color: #ec4899;
          border-color: #ec4899;
          transform: scale(1.05);
        }
        .image-info {
          padding: 12px;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          justify-content: space-between;
        }
        .image-name {
          color: #ffffff;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 8px;
          word-break: break-all;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .image-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: auto;
        }
        .image-folder-badge {
          background-color: rgba(59, 130, 246, 0.15);
          color: #60a5fa;
          font-size: 11px;
          padding: 2px 6px;
          border-radius: 4px;
          border: 1px solid rgba(59, 130, 246, 0.3);
          max-width: 100px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .image-size {
          color: #9ca3af;
          font-size: 11px;
        }
        .chip {
          padding: 6px 14px;
          border-radius: 9999px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid #374151;
          background-color: #1f2937;
          color: #9ca3af;
        }
        .chip.active {
          background-color: rgba(236, 72, 153, 0.15);
          color: #ec4899;
          border-color: #ec4899;
        }
        .chip:hover:not(.active) {
          background-color: #27323f;
          color: #ffffff;
        }
      `}</style>

      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ color: '#ffffff', fontSize: '28px', fontWeight: 'bold' }}>
            📸 Images Capturées
          </h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleTransfertBilletterie}
              disabled={transfertBilletterie}
              title="Synchronise les concerts 'À publier' (STATUS=publie) de Grist À venir vers Master Grist + upload flyer WP"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: transfertBilletterie ? '#4b5563' : '#7c3aed',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: transfertBilletterie ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => { if (!transfertBilletterie) e.currentTarget.style.backgroundColor = '#6d28d9' }}
              onMouseLeave={(e) => { if (!transfertBilletterie) e.currentTarget.style.backgroundColor = '#7c3aed' }}
            >
              {transfertBilletterie
                ? '⏳ Synchronisation...'
                : <>🔄 Synchroniser{aPublierCount !== null && aPublierCount > 0 && (
                    <span style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '10px', padding: '1px 7px', fontSize: '12px', fontWeight: '700' }}>
                      {aPublierCount} À publier
                    </span>
                  )}</>
              }
            </button>
            {transfertResult && (
              <div style={{
                position: 'fixed', bottom: '24px', right: '24px', zIndex: 1000,
                backgroundColor: transfertResult.errors?.length ? '#450a0a' : '#052e16',
                border: `1px solid ${transfertResult.errors?.length ? '#7f1d1d' : '#14532d'}`,
                borderRadius: '10px', padding: '16px 20px', minWidth: '280px',
                color: '#fff', fontSize: '14px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>
                      {transfertResult.errors?.length ? '⚠️ Transfert partiel' : '✅ Transfert terminé'}
                    </div>
                    <div style={{ color: '#a7f3d0' }}>{transfertResult.message}</div>
                    {transfertResult.errors?.length > 0 && (
                      <div style={{ color: '#fca5a5', marginTop: '6px', fontSize: '12px' }}>
                        {transfertResult.errors.slice(0, 3).map((e, i) => <div key={i}>{e}</div>)}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setTransfertResult(null)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '18px', cursor: 'pointer', marginLeft: '12px' }}>✕</button>
                </div>
              </div>
            )}
            <button
              onClick={() => { setShowGristSyncModal(true); setSyncGristResult(null); setSyncGristError(null) }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            >
              🔄 Synchroniser Grist
            </button>
            <button
              onClick={() => selectedSource !== null ? fetchImages(selectedSource) : fetchSourceCounts()}
              disabled={loadingImages}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#374151',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#374151'}
            >
              <RefreshCw size={16} />
              {loadingImages ? 'Chargement...' : 'Rafraîchir les images'}
            </button>
          </div>
        </div>

        {/* Captured Images View */}
        {true && (
          <>
            {/* ══ PARTIE 1 — SOURCES ORIGINALES (archives) ══ */}
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📂 Sources originales
                <span style={{ color: '#6b7280', fontSize: '13px', fontWeight: '400' }}>— sites scrappés + clippings Facebook</span>
              </h2>
              <p style={{ color: '#4b5563', fontSize: '12px', margin: '0 0 14px' }}>Images avant traitement — clic sur un onglet pour afficher</p>

              {/* Onglets sources — liste fixe, affichés même à 0 */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {SCRAPE_SOURCES.map(({ domain, label, emoji, gristUrl, countFrom }) => {
                  const count = countFrom === 'source'
                    ? (sourceCounts[domain] ?? 0)
                    : (archiveCounts[domain] ?? 0)
                  const isActive = selectedArchiveSource === domain
                  return (
                    <div key={domain} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      {/* Bouton sync Grist→À venir — SDM uniquement */}
                      {domain === 'sueurdemetal.com' && (
                        <button
                          onClick={handleSyncSDM}
                          disabled={syncingSDM}
                          title={syncSDMResult ? syncSDMResult.message : 'Importer les nouveaux concerts SDM dans À venir'}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: '28px', height: '32px',
                            backgroundColor: syncSDMResult ? (syncSDMResult.importes > 0 ? 'rgba(34,197,94,0.15)' : '#1f2937') : '#1f2937',
                            border: `1px solid ${syncSDMResult ? (syncSDMResult.importes > 0 ? '#22c55e' : '#374151') : '#374151'}`,
                            borderRadius: '9999px 0 0 9999px',
                            color: syncSDMResult ? (syncSDMResult.importes > 0 ? '#22c55e' : '#6b7280') : '#6b7280',
                            cursor: syncingSDM ? 'wait' : 'pointer', fontSize: '13px',
                            flexShrink: 0, transition: 'all 0.2s',
                          }}
                        >
                          {syncingSDM ? '…' : '⟳'}
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedArchiveSource(isActive ? null : domain)}
                        className={`chip ${isActive ? 'active' : ''}`}
                        style={{
                          fontSize: '13px', fontWeight: '600',
                          borderRadius: domain === 'sueurdemetal.com'
                            ? (gristUrl ? '0' : '0 9999px 9999px 0')
                            : (gristUrl ? '9999px 0 0 9999px' : '9999px'),
                          borderLeft: domain === 'sueurdemetal.com' ? 'none' : undefined,
                          borderRight: gristUrl ? 'none' : undefined,
                        }}
                      >
                        {emoji} {label} ({count})
                      </button>
                      {gristUrl && (
                        <a
                          href={gristUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Ouvrir Grist — ${label}`}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: '28px', height: '32px',
                            backgroundColor: isActive ? 'rgba(236,72,153,0.15)' : '#1f2937',
                            border: `1px solid ${isActive ? '#ec4899' : '#374151'}`,
                            borderRadius: '0 9999px 9999px 0',
                            color: isActive ? '#ec4899' : '#6b7280',
                            textDecoration: 'none', transition: 'all 0.2s', flexShrink: 0,
                          }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#27323f'; e.currentTarget.style.color = '#ffffff' }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = isActive ? 'rgba(236,72,153,0.15)' : '#1f2937'; e.currentTarget.style.color = isActive ? '#ec4899' : '#6b7280' }}
                        >
                          <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Filtre taille (FB Archives) ou semaine (autres sources) */}
              {!loadingArchive && archiveImages.length > 0 && (() => {
                const isFbArchive = selectedArchiveSource === 'facebook.com'

                // ── Tranches de taille ──
                const SIZE_RANGES = [
                  { key: '0-50',    label: '0–50 KB',      min: 0,    max: 50   },
                  { key: '50-100',  label: '50–100 KB',    min: 50,   max: 100  },
                  { key: '100-150', label: '100–150 KB',   min: 100,  max: 150  },
                  { key: '150-200', label: '150–200 KB',   min: 150,  max: 200  },
                  { key: '200-250', label: '200–250 KB',   min: 200,  max: 250  },
                  { key: '250-300', label: '250–300 KB',   min: 250,  max: 300  },
                  { key: '300-350', label: '300–350 KB',   min: 300,  max: 350  },
                  { key: '350-400', label: '350–400 KB',   min: 350,  max: 400  },
                  { key: '400-450', label: '400–450 KB',   min: 400,  max: 450  },
                  { key: '450-500', label: '450–500 KB',   min: 450,  max: 500  },
                  { key: '500-600', label: '500–600 KB',   min: 500,  max: 600  },
                  { key: '600-700', label: '600–700 KB',   min: 600,  max: 700  },
                  { key: '700-800', label: '700–800 KB',   min: 700,  max: 800  },
                  { key: '800-900', label: '800–900 KB',   min: 800,  max: 900  },
                  { key: '900-1000',label: '900 KB–1 Mo',  min: 900,  max: 1000 },
                  { key: '>1000',   label: '> 1 Mo',       min: 1000, max: Infinity },
                ]
                const activeSizeRanges = SIZE_RANGES.filter(r =>
                  archiveImages.some(img => img.size_kb >= r.min && img.size_kb < r.max)
                )

                // ── Semaines (sources non-FB) ──
                const weeks = Array.from(new Map(archiveImages.map(img => [img.week_number, img])).values())
                  .filter(img => img.week_number)
                  .sort((a, b) => (b.week_id || '').localeCompare(a.week_id || ''))

                const filtered = isFbArchive
                  ? (selectedArchiveSize === 'all'
                      ? archiveImages
                      : (() => {
                          const r = SIZE_RANGES.find(r => r.key === selectedArchiveSize)
                          return r ? archiveImages.filter(img => img.size_kb >= r.min && img.size_kb < r.max) : archiveImages
                        })())
                  : (selectedArchiveWeek === 'all'
                      ? archiveImages
                      : archiveImages.filter(img => img.week_number === selectedArchiveWeek))

                return (
                  <>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>
                        {isFbArchive ? '📦 Taille' : '🗓️ Semaine'}
                      </span>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {isFbArchive ? (
                          <>
                            <button onClick={() => setSelectedArchiveSize('all')} className={`chip ${selectedArchiveSize === 'all' ? 'active' : ''}`} style={{ fontSize: '11px' }}>
                              Toutes ({archiveImages.length})
                            </button>
                            {activeSizeRanges.map(r => {
                              const count = archiveImages.filter(img => img.size_kb >= r.min && img.size_kb < r.max).length
                              return (
                                <button key={r.key} onClick={() => setSelectedArchiveSize(r.key)} className={`chip ${selectedArchiveSize === r.key ? 'active' : ''}`} style={{ fontSize: '11px' }}>
                                  {r.label} ({count})
                                </button>
                              )
                            })}
                          </>
                        ) : (
                          <>
                            <button onClick={() => setSelectedArchiveWeek('all')} className={`chip ${selectedArchiveWeek === 'all' ? 'active' : ''}`} style={{ fontSize: '11px' }}>
                              Toutes ({archiveImages.length})
                            </button>
                            {weeks.map(img => {
                              const week = img.week_number!
                              const count = archiveImages.filter(i => i.week_number === week).length
                              return (
                                <button key={week} onClick={() => setSelectedArchiveWeek(week)} className={`chip ${selectedArchiveWeek === week ? 'active' : ''}`} style={{ fontSize: '11px' }}>
                                  {week} ({count})
                                </button>
                              )
                            })}
                          </>
                        )}
                      </div>
                    </div>
                    {/* Toolbar sélection */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          const allPaths = filtered.map(i => i.path)
                          const allSelected = allPaths.every(p => selectedArchivePaths.includes(p))
                          setSelectedArchivePaths(allSelected
                            ? selectedArchivePaths.filter(p => !allPaths.includes(p))
                            : [...new Set([...selectedArchivePaths, ...allPaths])])
                        }}
                        style={{ fontSize: '11px', padding: '4px 10px', backgroundColor: '#374151', color: '#d1d5db', border: '1px solid #4b5563', borderRadius: '6px', cursor: 'pointer' }}
                      >
                        {filtered.every(i => selectedArchivePaths.includes(i.path)) ? 'Désélectionner tout' : 'Tout sélectionner'}
                      </button>
                      {selectedArchivePaths.length > 0 && (
                        <>
                          <span style={{ color: '#9ca3af', fontSize: '11px' }}>{selectedArchivePaths.length} sélectionnée{selectedArchivePaths.length > 1 ? 's' : ''}</span>
                          <button
                            onClick={() => handleDeleteArchiveBatch(selectedArchivePaths)}
                            disabled={deletingArchive}
                            style={{ fontSize: '11px', padding: '4px 12px', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                          >
                            {deletingArchive ? '⏳ Suppression...' : `🗑️ Supprimer (${selectedArchivePaths.length})`}
                          </button>
                          <button
                            onClick={() => setSelectedArchivePaths([])}
                            style={{ fontSize: '11px', padding: '4px 8px', backgroundColor: 'transparent', color: '#6b7280', border: '1px solid #374151', borderRadius: '6px', cursor: 'pointer' }}
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>

                    <div className="image-grid">
                      {filtered.map((img) => {
                        const isSelected = selectedArchivePaths.includes(img.path)
                        return (
                        <div
                          key={img.path}
                          className="image-card"
                          style={{ outline: isSelected ? '2px solid #ec4899' : 'none', outlineOffset: '2px' }}
                        >
                          {/* Checkbox sélection */}
                          <div
                            onClick={() => setSelectedArchivePaths(prev =>
                              isSelected ? prev.filter(p => p !== img.path) : [...prev, img.path]
                            )}
                            style={{
                              position: 'absolute', top: '8px', left: '8px', zIndex: 10,
                              width: '20px', height: '20px', borderRadius: '4px', cursor: 'pointer',
                              backgroundColor: isSelected ? '#ec4899' : 'rgba(17,24,39,0.8)',
                              border: `2px solid ${isSelected ? '#ec4899' : '#6b7280'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '12px', color: '#fff', flexShrink: 0,
                            }}
                          >
                            {isSelected ? '✓' : ''}
                          </div>
                          {/* Bouton delete unitaire */}
                          <div className="image-card-hover-actions">
                            <button
                              className="image-card-action-btn"
                              title="Supprimer"
                              onClick={e => { e.stopPropagation(); handleDeleteArchiveBatch([img.path]) }}
                              style={{ backgroundColor: 'rgba(220,38,38,0.85)' }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                          <div className="image-thumbnail-container" onClick={() => setPreviewImage(img)}>
                            <img
                              src={`${import.meta.env.VITE_API_URL || 'http://localhost:8100'}/api/obsidian-clip/images/thumb/${img.path}`}
                              alt={img.name}
                              className="image-thumbnail"
                              loading="lazy"
                            />
                          </div>
                          <div className="image-info">
                            <div className="image-name" title={img.name}>{img.name}</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#9ca3af', fontSize: '10px', marginTop: '4px', borderTop: '1px solid #374151', paddingTop: '6px' }}>
                              <span style={{ color: img.size_kb < 50 ? '#f87171' : img.size_kb > 500 ? '#34d399' : '#9ca3af' }}>
                                📦 {img.size_kb} KB
                              </span>
                              <span>{img.name.split('.').pop()?.toUpperCase()}</span>
                            </div>
                          </div>
                        </div>
                        )
                      })}
                    </div>
                  </>
                )
              })()}

              {loadingArchive && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
                  <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #374151', borderTop: '3px solid #ec4899', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  <p style={{ marginTop: '12px', fontSize: '13px' }}>Chargement...</p>
                </div>
              )}
              {!loadingArchive && selectedArchiveSource === null && (
                <div style={{ padding: '24px', backgroundColor: '#111827', borderRadius: '8px', border: '1px dashed #374151', textAlign: 'center', color: '#4b5563', fontSize: '13px' }}>
                  Sélectionne une source pour voir ses images originales
                </div>
              )}
              {!loadingArchive && selectedArchiveSource !== null && archiveImages.length === 0 && (
                <div style={{ padding: '24px', backgroundColor: '#111827', borderRadius: '8px', border: '1px dashed #374151', textAlign: 'center', color: '#4b5563', fontSize: '13px' }}>
                  Aucune image pour cette source (notes uniquement)
                </div>
              )}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #1f2937', marginBottom: '32px' }} />

            {/* ══ SECTION FACEBOOK — catégories Grist ══ */}
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📘 Facebook
                <span style={{ color: '#6b7280', fontSize: '13px', fontWeight: '400' }}>— catégories Grist sync</span>
              </h2>
              <p style={{ color: '#4b5563', fontSize: '12px', margin: '0 0 14px' }}>Clips Facebook organisés par catégorie — clic sur un onglet pour afficher</p>

              {/* Onglets catégories + lien Grist */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {FB_CATEGORIES.map(({ cat, label, emoji, gristUrl }) => {
                  const count = fbCounts[cat] ?? 0
                  const isActive = selectedFbCat === cat
                  return (
                    <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <button
                        onClick={() => { setSelectedFbCat(isActive ? null : cat); setSelectedFbWeek('all') }}
                        className={`chip ${isActive ? 'active' : ''}`}
                        style={{ fontSize: '13px', fontWeight: '600', borderRadius: '9999px 0 0 9999px', borderRight: 'none' }}
                      >
                        {emoji} {label} ({count})
                      </button>
                      <a
                        href={gristUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Ouvrir Grist — ${label}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '28px',
                          height: '32px',
                          backgroundColor: isActive ? 'rgba(236,72,153,0.15)' : '#1f2937',
                          border: `1px solid ${isActive ? '#ec4899' : '#374151'}`,
                          borderRadius: '0 9999px 9999px 0',
                          color: isActive ? '#ec4899' : '#6b7280',
                          textDecoration: 'none',
                          transition: 'all 0.2s',
                          flexShrink: 0,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#27323f'; e.currentTarget.style.color = '#ffffff' }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = isActive ? 'rgba(236,72,153,0.15)' : '#1f2937'; e.currentTarget.style.color = isActive ? '#ec4899' : '#6b7280' }}
                      >
                        <ExternalLink size={11} />
                      </a>
                    </div>
                  )
                })}
              </div>

              {/* Filtre semaine + grille images Facebook */}
              {!loadingFb && fbImages.length > 0 && (() => {
                const weeks = Array.from(new Map(fbImages.map(img => [img.week_number, img])).values())
                  .filter(img => img.week_number)
                  .sort((a, b) => (b.week_id || '').localeCompare(a.week_id || ''))
                const filtered = selectedFbWeek === 'all'
                  ? fbImages
                  : fbImages.filter(img => img.week_number === selectedFbWeek)
                return (
                  <>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>
                        🗓️ Semaine
                      </span>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button onClick={() => setSelectedFbWeek('all')} className={`chip ${selectedFbWeek === 'all' ? 'active' : ''}`} style={{ fontSize: '11px' }}>
                          Toutes ({fbImages.length})
                        </button>
                        {weeks.map(img => {
                          const week = img.week_number!
                          const count = fbImages.filter(i => i.week_number === week).length
                          return (
                            <button key={week} onClick={() => setSelectedFbWeek(week)} className={`chip ${selectedFbWeek === week ? 'active' : ''}`} style={{ fontSize: '11px' }}>
                              {week} ({count})
                            </button>
                          )
                        })}
                      </div>
                    </div>
                    <div className="image-grid">
                      {filtered.map((img) => (
                        <div key={img.path} className="image-card">
                          <div className="image-thumbnail-container" onClick={() => setPreviewImage(img)}>
                            <img
                              src={`${import.meta.env.VITE_API_URL || 'http://localhost:8100'}/api/obsidian-clip/images/thumb/${img.path}`}
                              alt={img.name}
                              className="image-thumbnail"
                              loading="lazy"
                            />
                          </div>
                          <div className="image-info">
                            <div className="image-name" title={img.name}>{img.note_title || img.name}</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#9ca3af', fontSize: '10px', marginTop: '4px', borderTop: '1px solid #374151', paddingTop: '6px' }}>
                              <span>{img.week_number ? `🗓️ ${img.week_number}` : `📦 ${img.folder}`}</span>
                              <span>{img.size_kb} KB</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )
              })()}

              {loadingFb && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
                  <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #374151', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  <p style={{ marginTop: '12px', fontSize: '13px' }}>Chargement...</p>
                </div>
              )}
              {!loadingFb && selectedFbCat === null && (
                <div style={{ padding: '24px', backgroundColor: '#111827', borderRadius: '8px', border: '1px dashed #374151', textAlign: 'center', color: '#4b5563', fontSize: '13px' }}>
                  Sélectionne une catégorie pour voir les clips Facebook
                </div>
              )}
              {!loadingFb && selectedFbCat !== null && fbImages.length === 0 && (
                <div style={{ padding: '24px', backgroundColor: '#111827', borderRadius: '8px', border: '1px dashed #374151', textAlign: 'center', color: '#4b5563', fontSize: '13px' }}>
                  Aucune image dans cette catégorie pour le moment — elles apparaîtront ici lors du prochain clipping Facebook
                </div>
              )}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #1f2937', marginBottom: '32px' }} />

            {/* ══ SCREENSHOTS — envoyer une capture à Claude ══ */}
            <div style={{ marginBottom: '8px' }}>
              <h2 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📸 Envoyer une capture à Claude
                <span style={{ color: '#6b7280', fontSize: '13px', fontWeight: '400' }}>→ vault/SCREENSHOTS/</span>
              </h2>
              <p style={{ color: '#4b5563', fontSize: '12px', margin: '0 0 14px' }}>Stockées dans SCREENSHOTS/ — Claude sait toujours où les trouver</p>
            </div>

            {/* Upload Capture Section */}
            <div style={{
              backgroundColor: '#1f2937',
              border: '1px dashed #ec4899',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ImageIcon size={20} style={{ color: '#ec4899' }} />
                <h3 style={{ color: '#ffffff', margin: 0, fontSize: '16px', fontWeight: '600' }}>Importer une capture d'écran pour analyse IA</h3>
              </div>
              <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>
                Glissez-déposez ou sélectionnez une image (.png, .jpg, .webp). Elle sera enregistrée et indexée dans votre coffre Obsidian pour que je puisse la visualiser et vous aider à résoudre vos bugs !
              </p>

              <form onSubmit={handleUploadCapture} style={{
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap',
                alignItems: 'flex-end'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 200px' }}>
                  <label style={{ color: '#d1d5db', fontSize: '12px', fontWeight: '500' }}>Fichier image :</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setSelectedUploadFile(e.target.files[0])
                        if (!uploadTitle) {
                          const nameWithoutExt = e.target.files[0].name.replace(/\.[^/.]+$/, "")
                          setUploadTitle(nameWithoutExt)
                        }
                      }
                    }}
                    style={{
                      backgroundColor: '#111827',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      padding: '8px',
                      color: '#ffffff',
                      fontSize: '13px',
                      outline: 'none',
                      width: '100%'
                    }}
                    required
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '2 1 250px' }}>
                  <label style={{ color: '#d1d5db', fontSize: '12px', fontWeight: '500' }}>Description / Titre de la capture :</label>
                  <input
                    type="text"
                    placeholder="Ex: Erreur Grist de console ou Screenshot de bug..."
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    style={{
                      width: '100%',
                      backgroundColor: '#111827',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      color: '#ffffff',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 150px' }}>
                  <label style={{ color: '#d1d5db', fontSize: '12px', fontWeight: '500' }}>Tags (séparés par virgules) :</label>
                  <input
                    type="text"
                    value={uploadTags}
                    onChange={(e) => setUploadTags(e.target.value)}
                    style={{
                      width: '100%',
                      backgroundColor: '#111827',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      color: '#ffffff',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={uploadingImage || !selectedUploadFile}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: selectedUploadFile ? '#ec4899' : '#374151',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: selectedUploadFile ? 'pointer' : 'not-allowed',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: selectedUploadFile ? 1 : 0.6,
                    transition: 'all 0.2s',
                    height: '40px'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedUploadFile) e.currentTarget.style.backgroundColor = '#db2777'
                  }}
                  onMouseLeave={(e) => {
                    if (selectedUploadFile) e.currentTarget.style.backgroundColor = '#ec4899'
                  }}
                >
                  {uploadingImage ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Importation...
                    </>
                  ) : (
                    <>
                      <Plus size={14} />
                      Importer la capture
                    </>
                  )}
                </button>
              </form>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #1f2937', marginBottom: '32px' }} />

            {/* ══ PARTIE 2 — CAPTURES TRAITÉES ══ */}
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🗂️ Captures traitées
                <span style={{ color: '#6b7280', fontSize: '13px', fontWeight: '400' }}>— redimensionnées · classées par région & semaine</span>
                <a href="https://grist.megagenda.art/qTLv3Hi58wFL/FMA-Events-2026-A-venir" target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: '11px', color: '#60a5fa', backgroundColor: '#1e3a5f', border: '1px solid #2563eb', borderRadius: '4px', padding: '2px 7px', textDecoration: 'none', fontWeight: '500', flexShrink: 0 }}
                  title="Grist — FMA Events 2026 À venir"
                >↗ Grist À venir</a>
              </h2>
              <p style={{ color: '#4b5563', fontSize: '12px', margin: '0 0 14px' }}>
                Images dans CAPTURES/SEMAINES/ — {Object.values(semaineCounts).reduce((a,b)=>a+b,0)} images · {Object.keys(semaineCounts).length} régions
              </p>

              {/* Onglets régions */}
              {(() => {
                const REGION_LABELS: Record<string,string> = {
                  ALSACE:'Alsace',AQUITAINE:'Aquitaine',AUVERGNE:'Auvergne',
                  BOURGOGNE:'Bourgogne',BRETAGNE:'Bretagne',CENTRE:'Centre',
                  CHAMPAGNE_ARDENNE:'Champagne-Ardenne',FRANCHE_COMTE:'Franche-Comté',
                  LANGUEDOC_ROUSSILLON:'Languedoc-Roussillon',LIMOUSIN:'Limousin',
                  LORRAINE:'Lorraine',MIDI_PYRENEES:'Midi-Pyrénées',
                  NORMANDIE:'Normandie',NORD_HAUT_DE_FRANCE:'Nord / Hauts-de-France',
                  PACA:'PACA',PARIS_ILE_DE_FRANCE:'Paris / Île-de-France',
                  PAYS_DE_LA_LOIRE:'Pays de la Loire',PICARDIE:'Picardie',
                  POITOU_CHARENTES:'Poitou-Charentes',RHONES_ALPES:'Rhône-Alpes',
                  DOM_TOM:'DOM-TOM',SANS_REGION:'Sans région',
                }
                const sorted = Object.entries(semaineCounts)
                  .sort((a,b) => b[1]-a[1])
                return (
                  <>
                    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'16px' }}>
                      {sorted.map(([region, count]) => (
                        <button
                          key={region}
                          onClick={() => { setSelectedSemaineRegion(selectedSemaineRegion===region ? null : region); setSelectedSemaineWeek('all') }}
                          className={`chip ${selectedSemaineRegion===region ? 'active' : ''}`}
                          style={{ fontSize:'13px', fontWeight:'600' }}
                        >
                          {REGION_LABELS[region] || region} ({count})
                        </button>
                      ))}
                    </div>

                    {loadingSemaine && (
                      <div style={{ textAlign:'center', padding:'40px 0', color:'#6b7280' }}>
                        <div style={{ display:'inline-block', width:'32px', height:'32px', border:'3px solid #374151', borderTop:'3px solid #ec4899', borderRadius:'50%', animation:'spin 1s linear infinite' }}></div>
                        <p style={{ marginTop:'12px', fontSize:'13px' }}>Chargement...</p>
                      </div>
                    )}

                    {!loadingSemaine && selectedSemaineRegion === null && (
                      <div style={{ padding:'24px', backgroundColor:'#111827', borderRadius:'8px', border:'1px dashed #374151', textAlign:'center', color:'#4b5563', fontSize:'13px' }}>
                        Sélectionne une région pour voir ses captures traitées
                      </div>
                    )}

                    {!loadingSemaine && semaineImages.length > 0 && (() => {
                      const weeks = Array.from(new Map(semaineImages.map(img=>[img.week_id,img])).values())
                        .sort((a,b)=>(b.week_id||'').localeCompare(a.week_id||''))
                      const filtered = selectedSemaineWeek==='all'
                        ? semaineImages
                        : semaineImages.filter(img=>img.week_id===selectedSemaineWeek)
                      return (
                        <>
                          <div style={{ marginBottom:'12px' }}>
                            <span style={{ color:'#9ca3af', fontSize:'11px', fontWeight:'bold', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>
                              🗓️ Semaine
                            </span>
                            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                              <button onClick={()=>setSelectedSemaineWeek('all')} className={`chip ${selectedSemaineWeek==='all'?'active':''}`} style={{ fontSize:'11px' }}>
                                Toutes ({semaineImages.length})
                              </button>
                              {weeks.map(img=>{
                                const cnt = semaineImages.filter(i=>i.week_id===img.week_id).length
                                return (
                                  <button key={img.week_id} onClick={()=>setSelectedSemaineWeek(img.week_id||'')} className={`chip ${selectedSemaineWeek===img.week_id?'active':''}`} style={{ fontSize:'11px' }}>
                                    {img.week_number} ({cnt})
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                          <div className="image-grid">
                            {filtered.map(img=>(
                              <div key={img.path} className="image-card">
                                <div className="image-thumbnail-container" onClick={()=>setPreviewImage(img)}>
                                  <img
                                    src={`${import.meta.env.VITE_API_URL||'http://localhost:8100'}/api/obsidian-clip/images/thumb/${img.path}`}
                                    alt={img.name}
                                    className="image-thumbnail"
                                    loading="lazy"
                                  />
                                </div>
                                <div className="image-info">
                                  <div className="image-name" title={img.name}>{img.name}</div>
                                  <div style={{ display:'flex', justifyContent:'space-between', color:'#9ca3af', fontSize:'10px', marginTop:'4px', borderTop:'1px solid #374151', paddingTop:'6px' }}>
                                    <span>🗓️ {img.week_number}</span>
                                    <span>{img.size_kb} KB</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )
                    })()}
                  </>
                )
              })()}
            </div>

            {/* ── Onglets Sources (legacy — masqué) ── */}
            {false && images.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <span style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '10px' }}>
                  🌐 Source
                </span>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {availableSources.map(src => {
                    const count = sourceCounts[src] || 0
                    const label = SOURCE_LABELS[src] || src
                    return (
                      <button
                        key={src}
                        onClick={() => selectSource(selectedSource === src ? null : src)}
                        className={`chip ${selectedSource === src ? 'active' : ''}`}
                        style={{ fontSize: '13px', fontWeight: '600' }}
                      >
                        {label} ({count})
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* ── Si source sélectionnée : Région puis Semaine ── */}
            {!loadingImages && selectedSource !== null && (
              <>
                {/* Filtre Région */}
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>
                    📍 Région
                  </span>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button onClick={() => setSelectedRegion('all')} className={`chip ${selectedRegion === 'all' ? 'active' : ''}`}>
                      Toutes ({afterSourceFilter.length})
                    </button>
                    {availableRegions.map(reg => {
                      const count = afterSourceFilter.filter(img => getRegionKeyForImage(img) === reg.id).length
                      return (
                        <button key={reg.id} onClick={() => setSelectedRegion(reg.id)} className={`chip ${selectedRegion === reg.id ? 'active' : ''}`}
                          title={reg.name}>
                          {reg.name.split(' (')[0]} ({count})
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Filtre Semaine */}
                <div style={{ marginBottom: '16px', paddingLeft: selectedRegion !== 'all' ? '16px' : '0', borderLeft: selectedRegion !== 'all' ? '2px solid #ec4899' : 'none' }}>
                  <span style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>
                    🗓️ Semaine {selectedRegion !== 'all' ? `(${selectedRegion.replace(/_/g, ' ')})` : ''}
                  </span>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button onClick={() => selectWeek('all')} className={`chip ${selectedWeek === 'all' ? 'active' : ''}`}>
                      Toutes ({afterWeekFilter.length})
                    </button>
                    {availableWeeks.map(img => {
                      const week = img.week_number!
                      const count = afterSourceFilter.filter(i => i.week_number === week).length
                      return (
                        <button key={week} onClick={() => selectWeek(week)} className={`chip ${selectedWeek === week ? 'active' : ''}`}>
                          {week} ({count})
                        </button>
                      )
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Bulk Action Bar & Search */}
            {!loadingImages && images.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '24px',
                flexWrap: 'wrap',
                gap: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative', width: '280px' }}>
                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input
                      type="text"
                      placeholder="Rechercher une image..."
                      value={imageSearch}
                      onChange={(e) => setImageSearch(e.target.value)}
                      style={{
                        width: '100%',
                        backgroundColor: '#111827',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        padding: '8px 12px 8px 36px',
                        color: '#ffffff',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <button
                    onClick={() => handleSelectAll(filteredImages)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      backgroundColor: '#374151',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                  >
                    {filteredImages.length > 0 && filteredImages.every(img => selectedImages.includes(img.path)) ? (
                      <>
                        <Square size={16} />
                        Tout désélectionner
                      </>
                    ) : (
                      <>
                        <CheckSquare size={16} />
                        Tout sélectionner ({filteredImages.length})
                      </>
                    )}
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {selectedImages.length > 0 && (
                    <span style={{ color: '#ec4899', fontSize: '14px', fontWeight: '600' }}>
                      {selectedImages.length} image{selectedImages.length > 1 ? 's' : ''} sélectionnée{selectedImages.length > 1 ? 's' : ''}
                    </span>
                  )}
                  
                  <button
                    onClick={handleDownloadBatch}
                    disabled={selectedImages.length === 0}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      backgroundColor: selectedImages.length > 0 ? '#ec4899' : '#374151',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: selectedImages.length > 0 ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: selectedImages.length > 0 ? 1 : 0.5,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedImages.length > 0) e.currentTarget.style.backgroundColor = '#db2777'
                    }}
                    onMouseLeave={(e) => {
                      if (selectedImages.length > 0) e.currentTarget.style.backgroundColor = '#ec4899'
                    }}
                  >
                    <Download size={16} />
                    Télécharger la sélection (.zip)
                  </button>
                </div>
              </div>
            )}

            {/* Image Grid View */}
            {loadingImages ? (
              <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <div style={{
                  display: 'inline-block',
                  width: '40px',
                  height: '40px',
                  border: '4px solid #374151',
                  borderTop: '4px solid #ec4899',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ color: '#d1d5db', marginTop: '16px' }}>Chargement de vos images...</p>
              </div>
            ) : selectedSource === null ? (
              <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af', backgroundColor: '#1f2937', borderRadius: '12px', border: '1px dashed #374151' }}>
                <ImageIcon size={48} style={{ color: '#374151', marginBottom: '16px' }} />
                <h3 style={{ color: '#6b7280', fontSize: '16px', fontWeight: '500', margin: '0 0 8px' }}>Sélectionne une source pour voir les images</h3>
                <p style={{ fontSize: '13px', color: '#4b5563', margin: 0 }}>{Object.values(sourceCounts).reduce((a, b) => a + b, 0)} images disponibles — clique sur un onglet source ci-dessus</p>
              </div>
            ) : filteredImages.length > 0 ? (
              <div className="image-grid">
                {filteredImages.map((img) => {
                  const isSelected = selectedImages.includes(img.path)
                  return (
                    <div key={img.path} className="image-card">
                      {/* Selection Checkbox */}
                      <div
                        className={`image-card-checkbox ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleSelectImage(img.path)}
                      >
                        {isSelected && '✓'}
                      </div>

                      {/* Hover Quick Actions */}
                      <div className="image-card-hover-actions">
                        <button
                          className="image-card-action-btn"
                          onClick={() => setPreviewImage(img)}
                          title="Aperçu plein écran"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="image-card-action-btn"
                          onClick={(e) => { e.stopPropagation(); setMovingImage(img); setMoveTargetFolder('') }}
                          title="Déplacer vers un dossier"
                          style={{ color: '#34d399' }}
                        >
                          <Folder size={14} />
                        </button>
                        <button
                          className="image-card-action-btn"
                          onClick={() => handleDownloadSingle(img)}
                          title="Télécharger l'image"
                        >
                          <Download size={14} />
                        </button>
                        <button
                          className="image-card-action-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteImage(img)
                          }}
                          style={{ color: '#f87171' }}
                          title="Supprimer l'image et la note"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {/* Image Thumbnail Container */}
                      <div
                        className="image-thumbnail-container"
                        onClick={() => setPreviewImage(img)}
                      >
                        <img
                          src={`${import.meta.env.VITE_API_URL || 'http://localhost:8100'}/api/obsidian-clip/images/thumb/${img.path}`}
                          alt={img.name}
                          className="image-thumbnail"
                          loading="lazy"
                        />
                      </div>

                      {/* Information Container */}
                      <div className="image-info" style={{ padding: '12px', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <div className="image-name" style={{ fontSize: '13px', fontWeight: 'bold', color: '#ffffff', lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: '36px' }} title={img.group ? `${img.group} ${img.venue ? `@ ${img.venue}` : ''}` : img.name}>
                          {img.group ? (
                            <span>
                              <span style={{ color: '#ec4899' }}>{img.group}</span>
                              {img.venue && <span style={{ color: '#9ca3af', fontSize: '11px', display: 'block', fontWeight: 'normal', marginTop: '2px' }}>🏟️ {img.venue}</span>}
                            </span>
                          ) : img.name}
                        </div>
                        <div className="image-meta" style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: 'auto', paddingTop: '8px' }}>
                          {/* Badge statut synchro pour les images PICS/ */}
                          {img.is_pics && (
                            <div style={{ marginBottom: '4px' }}>
                              {img.sync_state === 'brute' && (
                                <span style={{ fontSize: '10px', backgroundColor: 'rgba(107,114,128,0.2)', color: '#9ca3af', padding: '2px 8px', borderRadius: '4px', border: '1px solid #374151' }}>
                                  ⚪ Brute
                                </span>
                              )}
                              {img.sync_state === 'locale' && (
                                <span title={`→ ${img.sync_renamed_to}`} style={{ fontSize: '10px', backgroundColor: 'rgba(245,158,11,0.15)', color: '#fbbf24', padding: '2px 8px', borderRadius: '4px', border: '1px solid rgba(245,158,11,0.3)', cursor: 'help' }}>
                                  🟡 Locale {img.sync_resized ? '• réduite' : ''}
                                </span>
                              )}
                              {img.sync_state === 'prête' && (
                                <span title={`→ ${img.sync_renamed_to}`} style={{ fontSize: '10px', backgroundColor: 'rgba(16,185,129,0.15)', color: '#34d399', padding: '2px 8px', borderRadius: '4px', border: '1px solid rgba(16,185,129,0.3)', cursor: 'help' }}>
                                  🟢 Prête WP
                                </span>
                              )}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {img.week_number && (
                              <span style={{ fontSize: '10px', backgroundColor: 'rgba(236, 72, 153, 0.15)', color: '#ec4899', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }} title={img.week_number}>
                                🗓️ {img.week_number.split(' (')[0]}
                              </span>
                            )}
                            {img.region_key && (
                              <span style={{ fontSize: '10px', backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                                📍 {img.region_key.replace(/_/g, ' ')}
                              </span>
                            )}
                            {!img.region_key && img.region && img.region !== "Sans Région" && (
                              <span style={{ fontSize: '10px', backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                                📍 {img.region}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#9ca3af', fontSize: '10px', marginTop: '4px', borderTop: '1px solid #374151', paddingTop: '6px' }}>
                            <span>📁 {img.folder || 'Racine'}</span>
                            <span>{img.sync_final_size_kb || img.size_kb} KB</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af', backgroundColor: '#1f2937', borderRadius: '12px', border: '1px solid #374151' }}>
                <ImageIcon size={48} style={{ color: '#4b5563', marginBottom: '16px' }} />
                <h3 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Aucune image trouvée</h3>
                <p style={{ maxWidth: '400px', margin: '0 auto', fontSize: '14px', lineHeight: '1.5' }}>
                  Aucune image ne correspond aux filtres sélectionnés.
                </p>
                <button
                  onClick={() => { setImageSearch(''); setSelectedWeek('all'); setSelectedRegion('all') }}
                  style={{
                    marginTop: '16px',
                    padding: '8px 16px',
                    backgroundColor: '#374151',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </>
        )}

        {/* Lightbox Modal */}
        {previewImage && (
          <div
            onClick={() => setPreviewImage(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(3, 7, 18, 0.9)',
              backdropFilter: 'blur(8px)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              animation: 'fadeIn 0.2s'
            }}
          >
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes zoomIn {
                from { transform: scale(0.95); opacity: 0; }
                to { transform: scale(1); opacity: 1; }
              }
            `}</style>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '16px',
                maxWidth: '90%',
                maxHeight: '90%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                animation: 'zoomIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                overflow: 'hidden'
              }}
            >
              <div style={{
                padding: '16px 24px',
                borderBottom: '1px solid #374151',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ImageIcon size={18} style={{ color: '#ec4899' }} />
                  <span style={{ color: '#ffffff', fontWeight: '600', fontSize: '15px', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {previewImage.name}
                  </span>
                </div>
                <button
                  onClick={() => setPreviewImage(null)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    fontSize: '20px',
                    cursor: 'pointer',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  ✕
                </button>
              </div>

              <div style={{
                padding: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#111827',
                overflow: 'auto',
                flexGrow: 1
              }}>
                <img
                  src={`${import.meta.env.VITE_API_URL || 'http://localhost:8100'}/api/obsidian-clip/images/file/${previewImage.path}`}
                  alt={previewImage.name}
                  onClick={() => handleDownloadSingle(previewImage)}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '60vh',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    cursor: 'pointer'
                  }}
                  title="Cliquer pour télécharger"
                />
              </div>

              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #374151',
                backgroundColor: '#1f2937',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  {previewImage.week_number && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Semaine de Com</span>
                      <span style={{ color: '#ec4899', fontSize: '13px', fontWeight: 'bold' }}>🗓️ {previewImage.week_number}</span>
                    </div>
                  )}
                  {previewImage.region && previewImage.region !== "Sans Région" && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Région / Ville</span>
                      <span style={{ color: '#60a5fa', fontSize: '13px', fontWeight: 'bold' }}>📍 {previewImage.region}</span>
                    </div>
                  )}
                  {previewImage.group && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Groupe / Événement</span>
                      <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: 'bold' }}>🎸 {previewImage.group}</span>
                    </div>
                  )}
                  {previewImage.date_formatted && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date Concert</span>
                      <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: '500' }}>📅 {previewImage.date_formatted}</span>
                    </div>
                  )}
                  {previewImage.venue && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lieu / Salle</span>
                      <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: '500' }}>🏟️ {previewImage.venue}</span>
                    </div>
                  )}
                  {previewImage.source_domain && previewImage.source_domain !== "Racine" && previewImage.source_domain !== "WEBCLIPS" && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Site Source</span>
                      {previewImage.source_url ? (
                        <a href={previewImage.source_url} target="_blank" rel="noopener noreferrer" style={{ color: '#ec4899', fontSize: '13px', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          🔗 {previewImage.source_domain} <ExternalLink size={12} />
                        </a>
                      ) : (
                        <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: '500' }}>🔗 {previewImage.source_domain}</span>
                      )}
                    </div>
                  )}
                  {previewImage.tags && previewImage.tags.length > 0 && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tags</span>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '2px' }}>
                        {previewImage.tags.map(t => (
                          <span key={t} style={{ backgroundColor: '#374151', color: '#f3f4f6', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: '500' }}>
                            #{t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <span style={{ color: '#9ca3af', fontSize: '11px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Taille</span>
                    <span style={{ color: '#ffffff', fontSize: '13px', fontWeight: '500' }}>💾 {previewImage.size_kb} KB</span>
                  </div>
                </div>

                 <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => { setMovingImage(previewImage); setMoveTargetFolder(''); setPreviewImage(null) }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '8px 16px', backgroundColor: '#059669',
                      color: '#ffffff', border: 'none', borderRadius: '8px',
                      cursor: 'pointer', fontSize: '14px', fontWeight: '500'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#047857'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  >
                    <Folder size={16} />
                    Déplacer
                  </button>
                  <button
                    onClick={() => handleDeleteImage(previewImage)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '8px 16px', backgroundColor: '#ef4444',
                      color: '#ffffff', border: 'none', borderRadius: '8px',
                      cursor: 'pointer', fontSize: '14px', fontWeight: '500'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                  >
                    <Trash2 size={16} />
                    Supprimer
                  </button>
                  <button
                    onClick={() => handleDownloadSingle(previewImage)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '8px 16px', backgroundColor: '#ec4899',
                      color: '#ffffff', border: 'none', borderRadius: '8px',
                      cursor: 'pointer', fontSize: '14px', fontWeight: '500'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#db2777'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ec4899'}
                  >
                    <Download size={16} />
                    Télécharger
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Résultat Synchro PICS */}
        {showSyncPicsResult && syncPicsResult && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1002,
            backdropFilter: 'blur(8px)', padding: '20px'
          }}>
            <div style={{
              backgroundColor: '#1f2937', borderRadius: '16px',
              border: '1px solid #374151', width: '100%', maxWidth: '600px',
              maxHeight: '80vh', display: 'flex', flexDirection: 'column', gap: '0',
              overflow: 'hidden'
            }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, color: '#ffffff', fontSize: '16px', fontWeight: 'bold' }}>
                  🖼️ Synchronisation PICS terminée
                </h3>
                <button onClick={() => setShowSyncPicsResult(false)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '20px', cursor: 'pointer' }}>✕</button>
              </div>
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                  <div style={{ flex: 1, textAlign: 'center', backgroundColor: '#111827', borderRadius: '8px', padding: '12px' }}>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#34d399' }}>{syncPicsResult.processed}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>traitées</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', backgroundColor: '#111827', borderRadius: '8px', padding: '12px' }}>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#60a5fa' }}>{syncPicsResult.skipped}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>déjà traitées</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', backgroundColor: '#111827', borderRadius: '8px', padding: '12px' }}>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: syncPicsResult.errors?.length ? '#f87171' : '#374151' }}>{syncPicsResult.errors?.length || 0}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>erreurs</div>
                  </div>
                </div>
                {syncPicsResult.items?.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ color: '#d1d5db', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Détail :</div>
                    {syncPicsResult.items.map((item: any, idx: number) => (
                      <div key={idx} style={{ backgroundColor: '#111827', borderRadius: '6px', padding: '8px 12px', fontSize: '12px' }}>
                        <div style={{ color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          📷 <span style={{ color: '#d1d5db' }}>{item.original}</span>
                        </div>
                        <div style={{ color: '#34d399', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          → <span style={{ color: '#6ee7b7' }}>{item.renamed_to}</span>
                          <span style={{ color: '#6b7280', marginLeft: '8px' }}>{item.region} · {item.week}</span>
                          {item.resized && <span style={{ color: '#fbbf24', marginLeft: '8px' }}>↓ {item.final_size_kb}KB</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {syncPicsResult.errors?.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ color: '#f87171', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Erreurs :</div>
                    {syncPicsResult.errors.map((err: any, idx: number) => (
                      <div key={idx} style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', color: '#fca5a5', marginBottom: '4px' }}>
                        {err.file}: {err.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #374151', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowSyncPicsResult(false)} style={{ padding: '8px 20px', backgroundColor: '#059669', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Move Image Modal */}
        {movingImage && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1001,
            backdropFilter: 'blur(8px)', padding: '20px'
          }}>
            <div style={{
              backgroundColor: '#1f2937', borderRadius: '16px',
              border: '1px solid #374151', width: '100%', maxWidth: '480px',
              padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, color: '#ffffff', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Folder size={18} style={{ color: '#34d399' }} />
                  Déplacer vers un dossier
                </h3>
                <button onClick={() => setMovingImage(null)}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '20px', cursor: 'pointer' }}>✕</button>
              </div>
              <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>
                <span style={{ color: '#ffffff', fontWeight: '500' }}>{movingImage.name}</span>
                <br/>La note associée sera également déplacée.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ color: '#d1d5db', fontSize: '13px', fontWeight: '600' }}>Dossier destination :</label>
                <select
                  value={moveTargetFolder}
                  onChange={(e) => setMoveTargetFolder(e.target.value)}
                  style={{
                    backgroundColor: '#111827', color: '#ffffff',
                    border: '1px solid #374151', borderRadius: '8px',
                    padding: '10px', fontSize: '14px', cursor: 'pointer', outline: 'none'
                  }}
                >
                  <option value="">-- Sélectionner un dossier --</option>
                  <optgroup label="── Dossiers sources ──">
                    {vaultFolders.filter((f: any) => f.group === 'Sources').map((f: any) => (
                      <option key={f.id} value={f.id}>📁 {f.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="── Semaines de com ──">
                    {vaultFolders.filter((f: any) => f.group === 'Semaines').map((f: any) => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #374151', paddingTop: '16px' }}>
                <button onClick={() => setMovingImage(null)}
                  style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  Annuler
                </button>
                <button
                  onClick={handleMoveImage}
                  disabled={!moveTargetFolder}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: moveTargetFolder ? '#059669' : '#374151',
                    color: '#ffffff', border: 'none', borderRadius: '8px',
                    cursor: moveTargetFolder ? 'pointer' : 'not-allowed',
                    fontSize: '14px', fontWeight: 'bold'
                  }}
                >
                  <Folder size={14} style={{ display: 'inline', marginRight: '6px' }} />
                  Déplacer ici
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Grist Sync Modal */}
        {showGristSyncModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            backdropFilter: 'blur(8px)', padding: '20px'
          }}>
            <div style={{
              backgroundColor: '#1f2937', borderRadius: '16px',
              border: '1px solid #374151', width: '100%', maxWidth: '480px',
              padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)'
            }}>

              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, color: '#ffffff', fontSize: '16px', fontWeight: 'bold' }}>
                  📊 Synchroniser vers Grist
                </h3>
                {!syncingGrist && (
                  <button onClick={() => { setShowGristSyncModal(false); setSyncGristResult(null); setSyncGristError(null) }}
                    style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                )}
              </div>

              {/* Sélecteur catégorie */}
              {!syncGristResult && !syncingGrist && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ color: '#d1d5db', fontSize: '13px', fontWeight: '600' }}>Catégorie source :</label>
                    <select
                      value={selectedGristFolder}
                      onChange={(e) => setSelectedGristFolder(e.target.value)}
                      style={{
                        backgroundColor: '#111827', color: '#ffffff',
                        border: '1px solid #374151', borderRadius: '8px',
                        padding: '10px 12px', fontSize: '14px',
                        cursor: 'pointer', outline: 'none', width: '100%'
                      }}
                    >
                      <option value="">-- Choisir une catégorie --</option>
                      {['Concerts', 'Facebook', 'Médias généraux'].map(group => (
                        <optgroup key={group} label={`── ${group} ──`}>
                          {GRIST_FOLDERS.filter(f => f.group === group).map(f => (
                            <option key={f.folder} value={f.folder}>{f.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #374151', paddingTop: '16px' }}>
                    <button onClick={() => setShowGristSyncModal(false)}
                      style={{ padding: '10px 16px', backgroundColor: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                      Annuler
                    </button>
                    <button
                      onClick={handleSyncGrist}
                      disabled={!selectedGristFolder}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: selectedGristFolder ? '#2563eb' : '#374151',
                        color: '#ffffff', border: 'none', borderRadius: '8px',
                        cursor: selectedGristFolder ? 'pointer' : 'not-allowed',
                        fontSize: '14px', fontWeight: 'bold'
                      }}>
                      🔄 Synchroniser
                    </button>
                  </div>
                </>
              )}

              {/* Spinner */}
              {syncingGrist && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ display: 'inline-block', width: '36px', height: '36px', border: '4px solid #374151', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '12px' }}></div>
                  <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>Import en cours...</p>
                </div>
              )}

              {/* Erreur */}
              {syncGristError && (
                <div style={{ padding: '12px', backgroundColor: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#f87171', fontSize: '14px' }}>
                  ⚠️ {syncGristError}
                </div>
              )}

              {/* Résultat */}
              {syncGristResult && !syncingGrist && (
                <>
                  {syncGristResult.details?.map((d: any, idx: number) => (
                    <div key={idx} style={{ backgroundColor: '#111827', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '28px', marginBottom: '4px' }}>✅</div>
                      <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '15px', marginBottom: '4px' }}>{d.label}</div>
                      <div style={{ color: '#ffffff', fontSize: '26px', fontWeight: '800' }}>+{d.added} ajouté{d.added !== 1 ? 's' : ''}</div>
                      {d.skipped_duplicates > 0 && (
                        <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>{d.skipped_duplicates} déjà présents ignorés</div>
                      )}
                      {d.errors?.length > 0 && (
                        <div style={{ color: '#f87171', fontSize: '13px', marginTop: '4px' }}>{d.errors.length} erreur(s)</div>
                      )}
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={() => { setSyncGristResult(null); setSyncGristError(null) }}
                      style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', marginRight: '8px' }}>
                      ← Retour
                    </button>
                    <button onClick={() => { setShowGristSyncModal(false); setSyncGristResult(null) }}
                      style={{ padding: '8px 20px', backgroundColor: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                      Fermer
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>
        )}
      </div>
    </div>
  )
}
